import React, { useRef, useEffect, useState, createContext } from 'react';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import { getCategories, searchLiveAuctions } from 'actions';
import TopSearchForm from 'components/Auctions/TopSearchForm';
import AuctionTile from 'components/LiveAuctions/AuctionTile';
import Pagination from 'components/partials/Pagination';
import { Loading } from 'components/Loading';
import MissingBlock from 'components/partials/MissingBlock';
import { get } from 'lodash';
import { withRouter } from 'next/router';
import useSearch from './hooks/useSearch';
import useHandleRedirects from '../AuctionSearchPage/hooks/useHandleRedirects';
import { Row, Col } from 'react-bootstrap';
import 'styles/components/live-auction.scss';
import 'styles/components/LiveAuctionTile.scss';

const MapHere = dynamic(() => import('components/MapHere'), { ssr: false });

export const LiveAuctionsSearchContext = createContext({});

const LiveAuctions = (props) => {
    const {
        searchLiveAuctions,
        total_records,
        auctions = [],
        loading,
        user_id,
        setStateName,
    } = props;
    const initialRender = useRef(true);
    const [pageHistory, setPageHistory] = useState([1]);

    const {
        searchState,
        setSearchState,
        searchObjUpdated,
        redoSearch,
        initialSearchState,
    } = useSearch({
        user_id,
        searchLiveAuctions,
        initialRender,
        liveAuctions: true,
    });
    useHandleRedirects({searchState, type: 'live'});

    useEffect(() => {
        const forceRefresh = () => {
            if (!localStorage.getItem('live_auctions_search_obj')) {
                setSearchState({
                    ...initialSearchState,
                    page_history: searchState.page_history.slice(0, searchState.page_history.length - 1),
                });
                localStorage.setItem('live_auctions_search_obj', JSON.stringify(initialSearchState.search_obj)),
                redoSearch({
                    search_obj_for_api: initialSearchState.search_obj,
                });
            }
        };
        forceRefresh();
    });

    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
        }
    }, []);

    const getAuctionLocations = () => {
        return auctions?.map((auction) => ({
            isFacilityLink: true,
            link: `/facilities/${ get(auction, 'facility_id') }#live-auctions`,
            facility_name: get(auction, 'facility.facility_name', ''),
            address: get(auction, 'facility.address'),
            city: get(auction, 'facility.city'),
            state: get(auction, 'facility.state'),
            zipcode: get(auction, 'facility.zipcode'),
            lat: get(auction, 'facility.marker.lat'),
            lng: get(auction, 'facility.marker.lng'),
            position: get(auction, 'facility.marker.position'),
        }));
    };

    const renderMap = () => {
        const locations = getAuctionLocations();

        if (!locations?.length) {
            return null;
        }

        return (
            <MapHere
                id="active-map"
                height="342px"
                width="100%"
                locations={locations}
            />
        );
    };

    const renderMissing = () => {
        const show = (total_records === 0);

        return (
            <MissingBlock show={show} />
        );
    };

    const handlePop = () => {
        if(searchState.page_history.length > 1) {
            setSearchState({
                ...searchState,
                search_obj: { ...searchState.search_obj, page_num: searchState.page_history[searchState.page_history.length -2] },
                page_history: searchState.page_history.slice(0, searchState.page_history.length - 1),
            });
        }
    };

    useEffect(() => {
        window.addEventListener('popstate', handlePop);

        return () => window.removeEventListener('popstate', handlePop);
    }, [searchState.page_history.length]);

    useEffect(() => {
        if (setStateName) {
            if (searchState?.search_obj?.term_state_label) {
                setStateName(searchState?.search_obj?.term_state_label);
            }
        }
    }, [searchState.search_obj]);


    return loading ? (
        <Loading loading_component={[{ label: 'Loading', loading: loading }]} />
    ) : (
        <LiveAuctionsSearchContext.Provider
            value={{
                searchState,
                setSearchState,
                redoSearch,
                ...props,
            }}
        >
            <TopSearchForm
                searchState={searchState}
                setSearchState={setSearchState}
                redoSearch={redoSearch}
                search_obj_updated={searchObjUpdated}
                search_obj={searchState.search_obj}
                getStateName={term_state_label => {
                    if (term_state_label !== searchState?.search_obj?.term_state_label) {
                        setSearchState({
                            ...searchState,
                            search_obj: {...searchState.search_obj, term_state_label},
                        });
                    }
                }}
                setCurrentState={state_name => {
                    setSearchState({
                        ...searchState,
                        search_obj: {...searchState.search_obj, state_name},
                    });
                }}
            />

            <div className="space-remove mt-2 pb-2">
                <h3 className="search-results-header">
                    Live Auctions Search Results
                </h3>

                <div className="search-results-total">
                    Total Auctions:
                    {' '}
                    {total_records}
                </div>
            </div>
            {renderMissing()}
            <div className="container-fluid mt-2">
                <div className="auction-search-map">
                    {renderMap()}
                </div>
                <div className="container-fluid">
                    <div className="col-md-10 offset-md-1">
                        <div className="row mt-2">
                            <div className="space-remove w-100" style={{ minHeight: 200 }}>
                                {auctions?.map((auction, index) => <AuctionTile key={index} auction={auction} />)}
                            </div>
                        </div>
                    </div>
                    <Row>
                        <Col className="d-flex align-items-center text-center" md={12}>
                            <Pagination
                                paginationPerPage={searchState?.search_obj?.page_count}
                                dataCount={total_records}
                                currentPage={parseInt(searchState?.search_obj?.page_num) || 1}
                                redoSearch={(page_num) => {
                                    setPageHistory([...pageHistory, page_num]);
                                    window.history.pushState({}, '', '/live-auctions');
                                    setSearchState({
                                        ...searchState,
                                        page_history: [...searchState.page_history, page_num],
                                        search_obj: { ...searchState.search_obj, page_num },
                                    });
                                }}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </LiveAuctionsSearchContext.Provider>
    );
};

const mapStateToProps = ({ live_auctions, user: { user_id }, page }) => {
    const {
        auctions,
        loading,
        error,
        categories,
        searchTypes,
        total_records,
    } = live_auctions;

    return {
        auctions,
        loading,
        error,
        categories,
        searchTypes,
        total_records,
        user_id,
        windowWidth: page.windowWidth,
    };
};

export default withRouter(connect(mapStateToProps, { getCategories, searchLiveAuctions })(LiveAuctions));
